import { TextField, TextFieldProps } from '@mui/material';
import { FormikHandlers, FormikState } from 'formik';

// Remove the properties that we end up manually setting as part of setting up formik
type ModifiedTextFieldProps = Omit<
  TextFieldProps,
  'name' | 'value' | 'onChange' | 'helperText' | 'error' | 'disabled'
>;

/**
 * The properties for the formik text field
 */
type FormikTextFieldProps<T extends object, K extends keyof T & string> = {
  /**
   * The formik object that's connected to the form that the text field will be within
   */
  formik: FormikState<T> & FormikHandlers;
  /**
   * The name of the field that's associated with this text field
   */
  name: K;

  /**
   * (Optional) This is optional helper text that can show to provide additional guidance
   * to the user
   */
  helpText?: string;

  /**
   * (Optional) Whether the form field is disabled. The field will always disable while
   *  the form is being submitted, regardless of this value.
   */
  disabled?: boolean;

  /**
   * (Optional) Add an additional on change trigger. This will occur prior to the onChange
   * that formik handles.
   */
  additionalOnChange?: (e: React.ChangeEvent<any>) => void;
} & ModifiedTextFieldProps;

/**
 * The formik text field is a component that should be used within a formik form. It makes
 *  setting up the form more standard and less prone to error.
 */
const FormikTextField = <T extends object, K extends keyof T & string>(
  props: FormikTextFieldProps<T, K>
) => {
  const { additionalOnChange, formik, helpText, disabled, ...rest } = props;

  return (
    <TextField
      value={formik.values[props.name]}
      onChange={(e) => {
        if (additionalOnChange) additionalOnChange(e);
        formik.handleChange(e);
      }}
      helperText={
        ((formik.touched[props.name] && formik.errors[props.name]) as string) ||
        helpText
      }
      disabled={formik.isSubmitting || disabled}
      error={
        formik.touched[props.name] &&
        Boolean(formik.errors[props.name] as string)
      }
      {...rest}
    />
  );
};

export default FormikTextField;
